import { useMemberInvoice } from "@gymflow/api";
import { currencies, formatCurrency } from "@gymflow/helpers";
import {
  DetailedInvoiceDTO,
  InvoiceNewDTO,
  InvoiceReceiptDTO,
  UserMemberBean,
} from "@gymflow/types";
import { ModalContext, useClubSettings } from "apps/portal/src/providers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import useGymflowModels from "apps/portal/src/store";
import { useFormik } from "formik";
import { LabeledFormikInput } from "libs/common/src/lib/components/molecules/FormikInput";
import { useContext } from "react";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { ApiResolverContext } from "../../../providers/ApiResolverProvider/ApiResolverContext";
import { ConfirmModal } from "../../templates";

export interface UserMemberAdjustInvoiceModalProps {
  userMember: UserMemberBean;
  invoice: InvoiceNewDTO | DetailedInvoiceDTO | InvoiceReceiptDTO;
}

export const UserMemberAdjustInvoiceModal = ({
  invoice,
  userMember,
}: UserMemberAdjustInvoiceModalProps) => {
  const { api } = useGymflowModels();
  const toast = useContext(ToastContext);
  const { clubId } = useContext(ApiResolverContext);

  const { adjustInvoiceMutation } = useMemberInvoice({
    api,
    memberId: userMember.id,
    clubId,
    invoiceNumber: invoice.number,
  });

  const { hide } = useContext(ModalContext);

  const { club } = useClubSettings();
  const formik = useFormik<{ targetPrice: number }>({
    initialValues: {
      targetPrice: invoice.dueAmount,
    },
    validationSchema: toFormikValidationSchema(
      z.object({
        targetPrice: z
          .number({
            coerce: true,
          })
          .max(
            invoice.totalAmount,
            `Price adjustment cannot be greater than the initial price (${formatCurrency(
              invoice.totalAmount,
              club.defaultCurrency,
            )})`,
          )
          .max(
            invoice.dueAmount,
            "The amount to credit must be between zero and the total amount due for the invoice",
          ),
      }),
    ),
    onSubmit: () => {},
  });
  return (
    <ConfirmModal
      isConfirmDisabled={!formik.isValid}
      onConfirm={async () => {
        if (formik.values.targetPrice === undefined) return;
        const targetPrice = Number(formik.values.targetPrice);
        await adjustInvoiceMutation.mutateAsync({
          invoiceNumber: invoice.number,
          amount: targetPrice,
        });
        toast.toast({
          message: "Invoice Adjusted Successfully.",
        });

        hide();
      }}
      title="Adjust Invoice"
      onCancel={() => {
        hide();
      }}
    >
      <div className="flex flex-col gap-4">
        <div>Enter the final amount you would like to be billed</div>
        <LabeledFormikInput
          label={`New Amount (${currencies[club.defaultCurrency].symbol})`}
          name="targetPrice"
          formikProps={formik as any}
          value={formik.values.targetPrice}
          onChange={(e) => {
            formik.setFieldValue("targetPrice", e.target.value);
          }}
          error={formik.errors.targetPrice}
        />
      </div>
    </ConfirmModal>
  );
};
